import { Service } from '@toeverything/infra';

import type { WorkspaceServerService } from '../../cloud';
import { defaultWorkspaceApiClient } from '../../cloud/services/workspace-api-client';
import type { WorkspaceService } from '../../workspace';

export type AccessRequestTarget = { kind: 'doc' | 'folder'; id: string };
export interface ObjectAccessRequest {
  id: string;
  generation: string;
  status: 'pending' | 'approved' | 'declined' | 'cancelled';
  requestedRole: 'Reader' | 'Editor';
  expiresAt: string;
  requesterId: string;
  requesterName: string | null;
  requesterEmail: string | null;
}

/** Online-only coordination; approval grants through the server's canonical grant services. */
export class ObjectAccessRequestService extends Service {
  constructor(
    private readonly workspace: WorkspaceService,
    private readonly server: WorkspaceServerService
  ) {
    super();
  }

  async request(
    target: AccessRequestTarget,
    role: 'Reader' | 'Editor' = 'Reader'
  ) {
    const response = await this.fetch('', {
      method: 'POST',
      body: JSON.stringify({ ...target, role }),
    });
    return (await response.json()) as {
      status: ObjectAccessRequest['status'] | 'already_allowed';
    };
  }

  async list(
    target: AccessRequestTarget,
    after?: string,
    signal?: AbortSignal
  ) {
    const query = new URLSearchParams({
      ...target,
      ...(after ? { after } : {}),
    });
    const response = await this.fetch(`?${query}`, { method: 'GET', signal });
    return (await response.json()) as {
      items: ObjectAccessRequest[];
      nextCursor: string | null;
    };
  }

  async resolve(
    request: Pick<ObjectAccessRequest, 'id' | 'generation'>,
    decision: 'approved' | 'declined'
  ) {
    await this.fetch(`/${encodeURIComponent(request.id)}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ generation: request.generation, decision }),
    });
  }

  private async fetch(suffix: string, init: RequestInit) {
    if (!this.server.server) throw new Error('Workspace server is unavailable');
    const workspaceId = this.workspace.workspace.id;
    return defaultWorkspaceApiClient.request({
      workspaceId,
      baseUrl: this.server.server.serverMetadata.baseUrl,
      path: `/api/workspace/${encodeURIComponent(workspaceId)}/access-requests${suffix}`,
      init: {
        ...init,
        headers: { 'content-type': 'application/json', ...init.headers },
      },
      category: 'metadata',
      key: init.method === 'GET' ? `access-request:${suffix}` : undefined,
      mode: 'foreground',
      priority: 'foreground',
    });
  }
}
